import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { formatLocaleHour, formatYearMonthDay } from '../../utils/date';
import { roundLength, animalDaysUnit, stockingPhaseTypes, THEME } from '../../config/commons';

import styles from './DataDetail.module.scss';
import DataItemDetail from './DataItemDetail';
import { calcAvgGrowth, roundAverageWeight } from './dataDetail.helpers';
import { Store } from '../../state/store.interface';
import { DEFAULT_DAYS_TO_INITIAL_STAGE } from '../../config/commons';

export default function DataDetail () {
  const [t] = useTranslation();

  const { analysis: analysisOriginal, theme } = useSelector((state: Store) => state.detail);

  const days = analysisOriginal.inputData?.stage + (DEFAULT_DAYS_TO_INITIAL_STAGE - 1);
  const analysisPhaseType = analysisOriginal.phaseType;
  const dictType = analysisPhaseType === stockingPhaseTypes.LARVAE ? 'larvae' : 'juvenile';
  const stage = analysisPhaseType === stockingPhaseTypes.LARVAE ? `${animalDaysUnit.PL} ${analysisOriginal.inputData?.stage} - ${days} ${t('analysis.days')}` : `${analysisOriginal.inputData?.stage} ${t('analysis.days')}`;

  const uniformityWeight = analysisOriginal.resultData.uniformity;
  const uniformityLength = 100 - analysisOriginal.resultData.variationCoefficientLength;
  const stockingWeight = analysisOriginal.sowingId?.weight;
  const averageWeight = analysisOriginal?.resultData?.averageWeight || 0;

  const isLightTheme = theme === THEME.LIGHT;

  const renderItem = (props: { value: string; label: string }) => {
    const { value, label } = props;

    return (
      <DataItemDetail value={value} label={label} theme={theme} />
    );
  };

  const renderCreatedAt = () => {
    if (analysisOriginal.isSieved) {
      return renderItem({ label: t('detail.sievingDate'), value: formatYearMonthDay(analysisOriginal.createdAt) });
    }

    return renderItem({ label: t('analysis.createdAt'), value: formatYearMonthDay(analysisOriginal.createdAt) });
  };

  const renderHour = () => {
    if (analysisOriginal.isSieved) {
      return renderItem({ label: t('detail.sievingHour'), value: formatLocaleHour(analysisOriginal.createdAt) });
    }

    return renderItem({ label: t('analysis.hour'), value: formatLocaleHour(analysisOriginal.createdAt) });
  };

  const renderDataDetail = () => {
    const gramPerWeek = calcAvgGrowth({ averageWeight, stage: analysisOriginal.inputData.stage, phaseType: analysisOriginal.phaseType, stockingWeight });

    return (
      <div className={cx(styles.gridContainer, isLightTheme ? styles.gridContainerLight : styles.gridContainerDark)}>
        <div className={cx(isLightTheme ? styles.oddLight : styles.oddDark, styles.firstRow)}>
          {renderItem({ label: t('analysis.resultData.sample'), value: `${analysisOriginal.resultData?.larvaeNumber} ${t(`analysis.resultData.${dictType}Sample`)} / ${analysisOriginal.inputData?.sampleWeight}` })}
          {analysisPhaseType === stockingPhaseTypes.LARVAE && renderItem({ label: t('analysis.resultData.larvaePerGram'), value: `${analysisOriginal.resultData.larvaePerGram}` })}
          {analysisPhaseType === stockingPhaseTypes.JUVENILE && renderItem({ label: t('analysis.inputData.maturation'), value: analysisOriginal.inputData?.maturationId?.name || '-' })}
          {analysisPhaseType === stockingPhaseTypes.ADULT && renderItem({ label: t('analysis.resultData.avgGrowth'), value: gramPerWeek })}

          {renderItem({ label: t(`analysis.inputData.${dictType}Stage`), value: stage })}
        </div>

        <div>
          {renderItem({ label: t('analysis.resultData.averageWeight'), value: `${roundAverageWeight({ averageWeight, phaseType: analysisPhaseType })}` })}
          {renderItem({ label: t('analysis.resultData.variationCoefficient'), value: `${analysisOriginal.resultData?.variationCoefficient.toFixed(2)} %` })}
          {renderItem({ label: `${t('detail.weight')} ${t('analysis.resultData.uniformity')}`, value: `${uniformityWeight.toFixed(2)} %` })}
        </div>

        <div className={isLightTheme ? styles.oddLight : styles.oddDark}>
          {renderItem({ label: t('analysis.resultData.averageLength'), value: `${roundLength({ value: analysisOriginal.resultData?.averageLength })}` })}
          {renderItem({ label: t('analysis.resultData.variationCoefficientLength'), value: `${analysisOriginal.resultData?.variationCoefficientLength.toFixed(2)} %` })}
          {renderItem({ label: `${t('detail.length')} ${t('analysis.resultData.uniformity')}`, value: `${uniformityLength.toFixed(2)} %` })}
        </div>

        <div>
          {renderItem({ label: t('analysis.campus'), value: analysisOriginal.campusId?.name?.toUpperCase() || '-' })}
          {renderCreatedAt()}
          {renderHour()}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderDataDetail()}
    </div>
  );
}
